/**
 * PRODUCT-154 — interface-theme state machine (leaf module).
 *
 * Deliberately NOT exported through the `src/reactive-variables` barrel: this is
 * a gql-free leaf so boot-static consumers (AntdI18n) can read the resolved
 * theme without dragging UI onto the entry chunk (PRO-130). Only `@apollo/client`
 * (makeVar) is imported here.
 *
 * THE APP IS LIGHT UNLESS THE USER PICKS DARK. There are two modes and the OS
 * is not one of them: `themeMode` is the user's explicit choice (light|dark)
 * and `resolvedTheme` is what it paints, which is now the same value. Both are
 * mirrored to localStorage + `<html data-theme>` so the pre-React boot script in
 * index.html paints the right theme with no flash, and this module re-reads the
 * same cache.
 *
 * `system` WAS a third mode and is retired. It made the app follow
 * `prefers-color-scheme`, which is the one thing this module must not do: the
 * shadcn surfaces were on the OS signal and the rest of the app on this one, so
 * a dark-OS machine could show a half-dark app. Nothing here reads the OS any
 * more — not this module, not `index.html`, and (since the `@custom-variant`
 * in `src/styles/tailwind.css`) not Tailwind's `dark:` utilities either.
 *
 * MIGRATION IS BY TOLERANCE, NOT BY REWRITE. A stored `system` — in
 * localStorage or in the backend `theme_preference` — is simply no longer a
 * valid mode, so `isThemeMode` rejects it and every reader falls back to
 * `light`, which is what the captain's "light unless you pick dark" means for
 * a user who had asked for "whatever the OS says". Nothing overwrites the
 * stored value: no data migration runs against the backend, and a read never
 * writes. If the user picks a mode, the normal write-through replaces it then.
 */
import { makeVar } from '@apollo/client'

export type ThemeMode = 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_MODE_STORAGE_KEY = 'themeMode'

/** The modes offered, in the order the UI offers them. */
export const THEME_MODES: ThemeMode[] = ['light', 'dark']

/** The mode any absent, unknown or RETIRED stored value resolves to. */
export const DEFAULT_THEME_MODE: ThemeMode = 'light'

export const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && THEME_MODES.includes(value as ThemeMode)

/** Tolerant read: anything that is not a live mode — absent, malformed, or the
 *  retired `system` — becomes `light` rather than throwing or wedging. */
export const coerceThemeMode = (value: unknown): ThemeMode =>
  isThemeMode(value) ? value : DEFAULT_THEME_MODE

// Absent/unknown/retired resolves to light — the default the boot script also
// uses, so a user who has never chosen a theme sees exactly the light app,
// deterministically (headless included).
const readStoredMode = (): ThemeMode => {
  try {
    return coerceThemeMode(localStorage.getItem(THEME_MODE_STORAGE_KEY))
  } catch {
    return DEFAULT_THEME_MODE
  }
}

/** Identity today — kept as the seam every consumer already calls, so a future
 *  mode that is not its own resolved theme has one place to land. */
export const resolveTheme = (mode: ThemeMode): ResolvedTheme => mode

const initialMode = readStoredMode()
export const themeModeVar = makeVar<ThemeMode>(initialMode)
export const resolvedThemeVar = makeVar<ResolvedTheme>(
  resolveTheme(initialMode)
)

const applyDataTheme = (resolved: ResolvedTheme): void => {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolved
  }
}

/** Persist + apply a theme choice. Local only — write-through to the backend is
 *  the caller's job (`useSetThemePreference`), so adoption never re-mutates. */
export const setThemeMode = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)
  } catch {
    /* private mode / storage disabled — the in-memory vars still drive the UI */
  }
  themeModeVar(mode)
  const resolved = resolveTheme(mode)
  resolvedThemeVar(resolved)
  applyDataTheme(resolved)
}
